import React, { useState } from 'react';
import { Form, Input, Button, Typography, Space, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { CognitoUser, AuthenticationDetails } from "amazon-cognito-identity-js";
import { useNavigate } from 'react-router-dom';
import { useDispatch } from "react-redux";
import UserPool from "../../utils/userPool";
import { login } from "../../redux/actions/authActions";
import { setUserData } from "../../redux/actions/profileActions";
import TokenParser from "../../utils/tokenParser";
import { setScopes } from "../../redux/actions/scopeActions";
import useTranslation from "../../hooks/useTranslation";

/**
 * Проверка почты без регулярного выражения: у вложенных квантификаторов время
 * разбора нелинейно, и вставленная в поле строка в мегабайт подвешивала вкладку.
 * Разбор по частям линеен и делает то же самое — «что-то, собака, что-то с точкой».
 */
const isEmailLike = (email) => {
    // Поле необязательное на уровне формы, поэтому сюда приходит и undefined:
    // прежняя регулярка на нём просто возвращала false, и это поведение сохраняем.
    if (typeof email !== 'string') {
        return false;
    }
    const parts = email.split('@');
    if (parts.length !== 2) {
        return false;
    }
    const [local, domain] = parts;
    const noSpaces = (value) => value.length > 0 && !/\s/.test(value);
    return noSpaces(local) && noSpaces(domain) && domain.includes('.') && !domain.startsWith('.')
        && !domain.endsWith('.');
};

const { Title } = Typography;

const LoginPage = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const onFinish = async (values) => {
        const { email, password } = values;

        // Простая проверка email
        if (!isEmailLike(email)) {
            message.error(t('auth.invalidEmail'));
            return;
        }

        setLoading(true);

        const user = new CognitoUser({
            Username: email,
            Pool: UserPool,
        });

        const authDetails = new AuthenticationDetails({
            Username: email,
            Password: password,
        });

        user.authenticateUser(authDetails, {
            onSuccess: async (data) => {
                const idToken = data.getIdToken().getJwtToken();
                const scopes = TokenParser.parseScopes(idToken);

                localStorage.setItem('token', idToken);
                dispatch(setScopes(scopes));
                dispatch(setUserData({
                    familyName: data.getIdToken().decodePayload().family_name,
                    givenName: data.getIdToken().decodePayload().given_name,
                    email,
                }));
                dispatch(login());
                message.success(t('auth.loginSuccess'));
                // Именно /deck: маршрута /dashboard в приложении нет, и вход работал
                // только через ловушку «всё остальное», по дороге теряя подсветку меню.
                navigate('/deck');
            },
            onFailure: (err) => {
                message.error(err.message);
                if (err.code === "UserNotConfirmedException") {
                    navigate("/confirm-email", { state: { email } });
                }
            },
            newPasswordRequired: (data) => {
                console.log("New password required: ", data);
            },
        });

        setLoading(false);
    };

    return (
        <div style={{ maxWidth: 360, margin: 'auto', padding: '2rem' }}>
            <Title level={2} style={{ textAlign: 'center' }}>{t('auth.signInTitle')}</Title>
            <Form onFinish={onFinish} layout="vertical">
                <Form.Item name="email">
                    <Input
                        prefix={<UserOutlined />}
                        placeholder={t('auth.email')}

                    />
                </Form.Item>
                <Form.Item
                    name="password"
                    rules={[{ required: true, message: t('auth.passwordRequired') }]}
                >
                    <Input.Password
                        prefix={<LockOutlined />}
                        placeholder={t('auth.password')}

                    />
                </Form.Item>
                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        block
                        loading={loading}
                    >
                        {t('auth.signIn')}
                    </Button>
                </Form.Item>
                <Space direction="horizontal" style={{ justifyContent: 'space-between', width: '100%' }}>
                    <Button type="text" onClick={() => navigate('/registration')}>
                        {t('auth.noAccount')}
                    </Button>
                    <Button type="text" onClick={() => navigate('/forgot-password')}>
                        {t('auth.forgotPassword')}
                    </Button>
                </Space>
            </Form>
        </div>
    );
};

export default LoginPage;
